import OpenInNew from "@mui/icons-material/OpenInNew";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { GetScoutConversationContextRoundtripsForStaffQuery } from "generated-graphql/graphql";
import { useMemo } from "react";

import {
  HISTORY_COLUMN_TOOLTIP,
  buildSentryTraceUrl,
  compactionKindFromJson,
  formatCostUsd,
  formatTokens,
} from "./scoutContextAnalysisFormat";

export type TurnRow = NonNullable<
  GetScoutConversationContextRoundtripsForStaffQuery["getScoutConversationContextRoundtripsForStaff"]
>["turns"][number];

export function RoundtripUsageTable({ rows: turns }: { rows: TurnRow[] }) {
  const latestId = turns.length > 0 ? turns[turns.length - 1]?.messageId : undefined;

  const columns = useMemo<CustomGridColDef<TurnRow>[]>(
    () => [
      {
        field: "seq",
        headerName: "Turn.RT",
        width: 80,
        valueGetter: (_value, row) =>
          row.turnNumber != null && row.roundtripIndex != null
            ? `${row.turnNumber}.${row.roundtripIndex}`
            : `${row.seq}`,
        renderCell: (params) => {
          const label =
            params.row.turnNumber != null && params.row.roundtripIndex != null
              ? `${params.row.turnNumber}.${params.row.roundtripIndex}`
              : `${params.row.seq}`;
          const isLatest = params.row.messageId === latestId;
          return (
            <Typography
              variant="body2"
              sx={{ fontWeight: isLatest ? 700 : 400, lineHeight: "inherit" }}
            >
              {label}
            </Typography>
          );
        },
      },
      {
        field: "model",
        headerName: "Model",
        width: 170,
        valueGetter: (_value, row) => row.model ?? "—",
      },
      {
        field: "traceId",
        headerName: "Trace",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.traceId ? (
            <Tooltip title="Open Sentry trace">
              <IconButton
                component="a"
                href={buildSentryTraceUrl(params.row.traceId)}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open Sentry trace"
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        field: "inputTokens",
        headerName: "Input",
        width: 90,
        type: "number",
        valueGetter: (_value, row) => row.inputTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "systemPromptTokens",
        headerName: "System prompt",
        width: 130,
        type: "number",
        valueGetter: (_value, row) => row.systemPromptTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "toolDefinitionsTokens",
        headerName: "Tool defs",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.toolDefinitionsTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "conversationHistoryTokens",
        headerName: "History",
        width: 100,
        type: "number",
        description: HISTORY_COLUMN_TOOLTIP,
        valueGetter: (_value, row) => row.conversationHistoryTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
        renderHeader: () => (
          <Tooltip title={HISTORY_COLUMN_TOOLTIP}>
            <span>History</span>
          </Tooltip>
        ),
      },
      {
        field: "compactionLocus",
        headerName: "Compaction",
        width: 120,
        description:
          "Fence (summary) or tool_clear on the call that consumed the smaller window. tool_clear is roundtrip metadata only — not a hydrate fence.",
        valueGetter: (_value, row) => {
          if (!row.compactionApplied) {
            return null;
          }
          const kind = compactionKindFromJson(row.compactionJson);
          if (kind === "tool_clear") {
            return "tool_clear";
          }
          return row.compactionLocus ?? "applied";
        },
        renderCell: (params) => {
          const locus = params.value;
          if (!locus) {
            return "—";
          }
          const json = params.row.compactionJson;
          const tooltip =
            json && typeof json === "object"
              ? JSON.stringify(json)
              : "This call's input followed a compaction fence";
          return (
            <Tooltip title={tooltip}>
              <span>{String(locus)}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "outputTokens",
        headerName: "Output",
        width: 90,
        type: "number",
        valueGetter: (_value, row) => row.outputTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "thinkingTokens",
        headerName: "Thinking",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.thinkingTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "cacheReadTokens",
        headerName: "Cache read",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.cacheReadTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "cacheWriteTokens",
        headerName: "Cache write",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.cacheWriteTokens ?? 0,
        valueFormatter: (value) => formatTokens(value),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Cost",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedCostUsd,
        valueFormatter: (value) => formatCostUsd(value),
      },
    ],
    [latestId]
  );

  return (
    <DataGrid<TurnRow>
      rows={turns}
      getRowId={(row) => row.messageId}
      columns={columns}
      hideFooter
      disableRowSelectionOnClick
      noRowsMessage="No assistant turns yet."
      getRowClassName={(params) => (params.id === latestId ? "scout-latest-roundtrip" : "")}
      sx={{
        border: "none",
        height: "100%",
        "& .scout-latest-roundtrip": {
          backgroundColor: "action.hover",
        },
      }}
    />
  );
}
